// Global Instructions Rule Applied!
//
// Opening a cited interaction from an agent signal or memory.
//
// An interaction has no form of its own — it lives in its parent record's
// timeline (Dashboard tab). The app already deep-links into that timeline:
// `?interaction=<id>` on the parent's full-form URL makes InteractionsSection
// open the detail panel on that card, exactly as a click would (the proposal
// review Open button uses the same route — see
// agent_access/utils/proposalPresentation.tsx `openTargetFor`). This hook
// resolves a signal's / follower's context into that target and opens it
// through the tab manager. Contexts with no timeline (funds, fund groups,
// pipeline meeting series) resolve to nothing, and the callers render the
// citation as plain text rather than a link to nowhere.
import { useCallback } from 'react'
import { useOptionalTabManagerContext } from '../../../core/app-layout/lib/TabManagerContext.tsx'
import { interactionOpenTarget, TimelineRef } from '../utils/interactionOpenTarget.ts'

export { interactionOpenTarget } from '../utils/interactionOpenTarget.ts'
export type { InteractionOpenTarget, TimelineRef } from '../utils/interactionOpenTarget.ts'

type OpenFormInTab = (module: string, id: string, title: string | null, query: Record<string, string>) => void

/**
 * A click handler that opens `interactionId` on its timeline (the citation's
 * own, else this record's), or undefined when there is no tab manager to
 * navigate with. Callers decide link-vs-text per citation with
 * `interactionOpenTarget`, so a citation with nowhere to go never renders as
 * a dead link.
 */
export const useOpenInteraction = (
  contextTable: string | null | undefined,
  contextId: string | null | undefined
): ((interactionId: string, timeline?: TimelineRef | null) => void) | undefined => {
  const tabManager = useOptionalTabManagerContext() as { openFormInTab?: OpenFormInTab } | null
  const openFormInTab = tabManager?.openFormInTab

  const handler = useCallback(
    (interactionId: string, timeline?: TimelineRef | null) => {
      const target = interactionOpenTarget(contextTable, contextId, interactionId, timeline)
      if (!target || !openFormInTab) return
      openFormInTab(target.module, target.id, null, target.query)
    },
    [contextTable, contextId, openFormInTab]
  )

  return openFormInTab ? handler : undefined
}
